import _ from 'lodash';
import { makePrivateRequestAsManager, makePrivateRequestAsUser, privateAuthenticatorRequest, publicAuthenticatorRequest } from './managers/requestBuilder';
import { getRegionForRequest, getUid } from './managers/store';

// Uids of Videoloft demo and reviewer accounts
const VIDEOLOFT_DEMO_UIDS = process.env.TEST_DEMO_UIDS ? JSON.parse(process.env.TEST_DEMO_UIDS) : [
    // Reviewer accounts
    1324475, // uswest2
    1325047, // euwest1
    1325046, // useast1

    // Regional demos
    1324936, // uswest2
    1325044, // euwest1,
    1325045, // useast1
];

export const isVideoloftDemoUid = (uid) => VIDEOLOFT_DEMO_UIDS.includes(Number(uid));

const getPermissionCameraName = (userAccess) => {
    return userAccess.object_name
        ?? userAccess.object_alias
        ?? userAccess.object_camera_name
        ?? userAccess.object_device_name
        ?? userAccess.camera_name
        ?? userAccess.device_name
        ?? null;
};

/**
 * Get users who have access to the specified user's cameras.
 * Returns array of objects, each with user's name, email, uid, and devices (an object mapping device ids to access level).
 * @param {import('./managers/requestBuilder').PrivateRequestData} requestData
 * @param {boolean} [excludeVideoloftDemoUids] Exclude Videoloft demo accounts, if necessary, from results.
 * @returns {object[]}
 */
export const getUsersWithAccess = async ({ makePrivateRequest, managerUid, region }, uid=getUid(), excludeVideoloftDemoUids = true) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/multiuser/${uid}/permissions`,
        {
            region: managerUid ? region : getRegionForRequest(uid) // if managerUid is present then it's a staff request so use the region passed in
        }
    );

    // Object structured with keys being user ids and values being objects with user access info
    const users = {};

    // res is array of user/device mappings
    // Iterate through and populate users object
    for (const userAccess of data.result) {

        // If multiuser access has been granted to this camera for our demos, exclude it if specified in arguments
        if (excludeVideoloftDemoUids && VIDEOLOFT_DEMO_UIDS.includes(userAccess.subject_uid)) {
            continue;
        }

        // Add new user if not already in users object
        if (!users[userAccess.subject_uid]) {
            users[userAccess.subject_uid] = {
                email: userAccess.email,
                name: userAccess.name,
                uid: userAccess.subject_uid,
                devices: {},
                deviceNames: {},
            };
        }

        // For operation v, 'r,v' is returned
        // Replace this with just 'v'
        const processOperation = (operation) =>
            operation === 'r,v' || operation === 'v,r' ? 'v' : operation;

        // Device id of 0 means that user has access to all cameras
        if (userAccess.object_deviceid === 0) {
            users[userAccess.subject_uid].devices.all = processOperation(
                userAccess.operation
            );
        } else {
            // Otherwise key is just device id
            const deviceId = userAccess.object_deviceid;
            users[userAccess.subject_uid].devices[deviceId] = processOperation(userAccess.operation);
            const cameraName = getPermissionCameraName(userAccess);
            if (cameraName) {
                users[userAccess.subject_uid].deviceNames[deviceId] = cameraName;
            }
        }
    }

    return Object.values(users);
};

export const getUsersWithAccessToLoggedInUser = makePrivateRequestAsUser(getUsersWithAccess);
export const getUsersWithAccessAsStaff = (uid, region) => makePrivateRequestAsManager(getUsersWithAccess, { uid, region })(uid);

/**
 * Get users who have used to have access to the specified user's cameras.
 * Returns array of objects, each with user's name, email, uid, and devices (an object mapping device ids to access level).
 * @param {import('./managers/requestBuilder').PrivateRequestData} requestData
 * @returns {object[]}
 */
export const getUsersWithRevokedAccess = async ({ makePrivateRequest }, uid=getUid(), excludeVideoloftDemoUids = true) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/multiuser/${uid}/revoked-permissions`,
        {
            region: getRegionForRequest(uid)
        }
    );

    // Object structured with keys being user ids and values being objects with user access info
    const users = {};

    // res is array of user/device mappings
    // Iterate through and populate users object
    for (const userAccess of data.result) {

        // Add new user if not already in users object
        if (!users[userAccess.subject_uid]) {
            users[userAccess.subject_uid] = {
                email: userAccess.email,
                uid: userAccess.subject_uid,
                devices: {},
            };
        }

        // For operation v, 'r,v' is returned
        // Replace this with just 'v'
        const processOperation = (operation) =>
            operation === 'r,v' ? 'v' : operation;

        // Device id of 0 means that user has access to all cameras
        if (userAccess.object_deviceid === 0) {
            users[userAccess.subject_uid].devices.all = processOperation(
                userAccess.operation
            );
        } else {
            // Otherwise key is just device id
            users[userAccess.subject_uid].devices[userAccess.object_deviceid] =
                processOperation(userAccess.operation);
        }
    }

    return Object.values(users);
};

export const getUsersWithRevokedAccessToLoggedInUser = makePrivateRequestAsUser(getUsersWithRevokedAccess);

// Get users who have pending invites to the specified user's cameras
const getUsersWithPendingInvites = async ({ makePrivateRequest, managerUid, region }, uid=getUid()) => {
    const { data } = await makePrivateRequest(
        'auth',
        'GET',
        `/multiuser/${uid}/permissions/pending`,
        {
            region: managerUid ? region : getRegionForRequest(uid) // if managerUid is present then it's a staff request so use the region passed in
        }
    );

    return data.map(({ id, email, access }) => ({
        inviteId: id,
        email,
        access: _.chain(access)
          .groupBy(({ deviceId }) => (deviceId === 0 ? 'all' : deviceId))
          .mapValues(items => items.map(item => item.operation).join(','))
          .value(),
      }));
};
export const getUsersWithPendingInvitesFromLoggedInUser = makePrivateRequestAsUser(getUsersWithPendingInvites);
export const getUsersWithPendingInvitesAsStaff = makePrivateRequestAsManager(getUsersWithPendingInvites);

/**
 * Grant user access to one or more cameras
 * @param {string} email Email of user receiving permissions.
 * @param {object} devicePermissions Object mapping device id to permission ('a', 'r' or 'v'). Use id 0 to mean all cameras.
 * @param {boolean} restrictSSOOrganistion Only relevant to camera owners that are part of an SSO (e.g. Azure) organisation.
 *                                         Enforces that recipient must be part of same organisation.
 * @returns 
 */
export const grantUserAccess = makePrivateRequestAsUser(async (
    { makePrivateRequest },
    email,
    uid=getUid(),
    devicePermissions,
    restrictSSOOrganistion = false,
) => {
    const { data } = await makePrivateRequest(
        'auth',
        'POST',
        `/multiuser/${uid}/permissions`,
        {
            data: {
                viewerEmail: email,
                // All cameras = device id 0
                permissions: Object.entries(devicePermissions).map(
                    ([id, operation]) => ({
                        deviceId: id === 'all' ? 0 : id,
                        operation,
                    })
                ),
                ssoOrgMatch: restrictSSOOrganistion,

                // Some corporate systems were blocking invite emails because the invite link contained their own URL (in the query string email address)
                excludeEmail: true
            },
            region: getRegionForRequest(uid)
        }
    );

    if (data.status === null) {
        throw new Error();
    }

    return {
        uid: data.uid,
        status: data.status,
        inviteId: data.id,
    };
});
/**
 * Grant user access to install and additional users to another account
 * @param {string} email Email of user receiving permissions.
 * @param {boolean} restrictSSOOrganisation Only relevant to camera owners that are part of an SSO (e.g. Azure) organisation.
 *                                         Enforces that recipient must be part of same organisation.
 * @returns 
 */
export const makeCoOwner = async (
    email,
    restrictSSOOrganisation = false
) => {
    const { data } = await privateAuthenticatorRequest(
        'POST',
        `/multiuser/:uid/account-permissions`,
        {
            data: {
                viewerEmail: email,
                permissions: ['c'],
                ssoOrgMatch: restrictSSOOrganisation,

                // Some corporate systems were blocking invite emails because the invite link contained their own URL (in the query string email address)
                excludeEmail: true
            },
        }
    );

    if (data.status === null) {
        throw new Error();
    }

    return {
        uid: data.uid,
        status: data.status,
        inviteId: data.id,
    };
};

/**
 * Revoke user access to install and additional users to another account
 * @param {string} viewerUserId Uid of user with permissions.
 * @returns 
 */
 export const revokeCoOwnerAccess = async (
    viewerUserId
) => {
    return await privateAuthenticatorRequest(
        'DELETE',
        `/multiuser/:uid/account-permissions`,
        {
            data: {
                viewerUserId,
                permissions:['c']
            },
        }
    );
};
// Revoke user access to certain or all devices
// If deviceIds is undefined, all permissions granted to user with uid viewerId will be revoked
export const revokeUserAccess = makePrivateRequestAsUser(async (
    { makePrivateRequest },
    viewerUserId,
    uid=getUid(),
    deviceIds
) => {
    return (
        await makePrivateRequest(
            'auth',
            'DELETE',
            `/multiuser/${uid}/permissions`,
            {
                params: {
                    viewerUserId,
                    deviceIds,
                },
                region: getRegionForRequest(uid)
            }
        )
    ).data;
});

export const revokeInvite = async (inviteId,uid=getUid()) => {
    return (
        await privateAuthenticatorRequest(
            'POST',
            `/multiuser/${uid}/invite/${inviteId}/revoke`
        )
    ).data;
};

// Returns uid of invite sender
export const acceptInvite = async (
    inviteId,
    inviteCode
) => {
    const { data } = await privateAuthenticatorRequest(
        'POST',
        `/multiuser/:uid/invite/${inviteId}/accept`,
        {
            data: {
                inviteCode,
            }
        }
    );

    return data.objectUid;
};

export const checkInviteStatus = async (
    inviteId,
    inviteCode,
    region
) => {
    try {
        const { data: { exists, provider, email } } = await publicAuthenticatorRequest(
            'GET',
            `/multiuser/invite/${inviteId}/validate`,
            {
                params: {
                    inviteCode,
                },
            },
            region
        );

        return { userExists: exists, ssoProvider: provider, email };
    } catch (error) {
        console.error(error);
        if (error.response?.status === 404) {
            // Invalid invite
            return { error: true };
        }
        throw error;
    }
};


/* ----------------------------- Demo consenting ---------------------------- */

/**
 * Get array of device ids owned by logged in user that are opted in to VL demo.
 * @returns {Promise<number[]>} Array of device ids.
 */
export const getCamerasOptedInToDemo = makePrivateRequestAsUser(async () => {
    const usersWithAccess = await getUsersWithAccessToLoggedInUser(undefined, false);

    const camerasOptedIn = usersWithAccess.reduce((acc, { uid, devices }) => {
        if (VIDEOLOFT_DEMO_UIDS.includes(uid)) {
            return _.uniq(acc.concat(_.keys(devices).map(deviceId => parseInt(deviceId))));
        }

        return acc;
    }, []);

    return camerasOptedIn;
});

/**
 * Opt in cameras owned by logged in user to VL demo.
 * @param {number[]} deviceIds Device ids to opt in.
 * @returns Axios promise.
 */
export const optCamerasInToDemo = makePrivateRequestAsUser(async ({ makePrivateRequest }, deviceIds) => {
    return makePrivateRequest(
        'auth',
        'POST',
        '/demo/users/:uid/cameras',
        {
            data: {
                deviceIds
            }
        }
    );
});

/**
 * Opt out cameras owned by logged in user from VL demo.
 * @param {number[]} deviceIds Device ids to opt out.
 * @returns Axios promise.
 */
export const optCamerasOutOfDemo = makePrivateRequestAsUser(async ({ makePrivateRequest }, deviceIds) => {
    return makePrivateRequest(
        'auth',
        'DELETE',
        '/demo/users/:uid/cameras',
        {
            data: {
                deviceIds
            }
        }
    );
});
